// src/App.tsx
// Router setup — protected routes redirect to /login if not authenticated

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import './styles/globals.css';

// Pages
import LoginPage        from './pages/LoginPage';
import DashboardPage    from './pages/DashboardPage';
import WeekPage         from './pages/WeekPage';
import SummaryPage      from './pages/SummaryPage';
import FamilyFundPage   from './pages/FamilyFundPage';
import HabitsConfigPage from './pages/HabitsConfigPage';
import PrioritiesPage   from './pages/PrioritiesPage';
import WinLogPage       from './pages/WinLogPage';
import ReflectionPage   from './pages/ReflectionPage';
import AdminUsersPage   from './pages/AdminUsersPage';
import SettingsPage     from './pages/SettingsPage';

// Layout
import AppLayout from './components/AppLayout';

// ── PROTECTED ROUTE ───────────────────────────────────────────────

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', background: 'var(--bg)', color: 'var(--text3)',
        fontFamily: 'var(--font-mono)', fontSize: '12px',
      }}>
        loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// ── ADMIN ROUTE ───────────────────────────────────────────────────

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (user?.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

// ── APP ───────────────────────────────────────────────────────────

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<LoginPage resetMode />} />

          {/* Protected — inside app layout (sidebar + main) */}
          <Route path="/" element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard"         element={<DashboardPage />} />
            <Route path="week"              element={<WeekPage />} />
            <Route path="summary"           element={<SummaryPage />} />
            <Route path="family-fund"       element={<FamilyFundPage />} />
            <Route path="habits/config"     element={<HabitsConfigPage />} />
            <Route path="habits/priorities" element={<PrioritiesPage />} />
            <Route path="win-log"           element={<WinLogPage />} />
            <Route path="reflection"        element={<ReflectionPage />} />
            <Route path="settings"          element={<SettingsPage />} />
            <Route path="admin/users"       element={
              <AdminRoute><AdminUsersPage /></AdminRoute>
            } />
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
